/* eslint-disable react-hooks/exhaustive-deps */
import { useState } from "react";
import { Breadcrumb } from "../../components/ui";
import {
  useActivateEventMutation,
  useDeleteEventAndRecurrentsMutation,
  useDeleteEventMutation,
  useGetEventByIdQuery,
  useGetRecurrentEventsQuery,
} from "../../store/services/events";
import { getEventImageSrc } from "../../utils/functions/getEventImageSrc";
import { useGetTenantByIdQuery } from "../../store/services/tenants";
import { useGetEventTypesQuery } from "../../store/services/events-types";
import { useNavigate, useParams } from "react-router-dom";
import { Badge } from "primereact/badge";
import {
  correctDateAndHours,
  extractHoursAndMinutes,
  formatDateWithoutSeconds,
} from "../../utils/functions/date";
import { ProgressSpinner } from "primereact/progressspinner";

import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import {
  DuplicateEventForm,
  EventMessagesDV,
  EventCancelledDV,
  InscriptionsDT,
  EventInterestDT,
} from "../../components";
import { Image } from "primereact/image";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';

import { SingleSelectUser } from "../../components/SingleSelectUser";
import { CancelEventDialog } from "../../components/Dialogs/CancelEventDialog";
import { EventCancellationDetails } from "../../components/EventCancellationDetails";
import toast from "react-hot-toast";
import { Event } from "../../models/events";
import { RadioButton } from "primereact/radiobutton";
import { useGetVenueByIdQuery } from "../../store/services/venues";

export const EventDetail = () => {
  const { eventId } = useParams();
  const [hostToAdd, setHostToAdd] = useState<number>();
  const [duplicateEvent, setDuplicateEvent] = useState<Event>();
  const [eventToDelete, setEventToDelete] = useState<Event>();
  const [showChangeEventStateDialog, setShowChangeEventStateDialog] = useState<boolean>();
  const [showCancelEventDialog, setShowCancelEventDialog] = useState(false);
  const [deleteWithRecurrents, setDeleteWithRecurrents] = useState<boolean>(false);
  const navigate = useNavigate();

  const {
    data,
    isLoading,
  } = useGetEventByIdQuery(eventId || "");

  const {
    data: recurrentEvents
  } = useGetRecurrentEventsQuery(data?.id || 0, {
    skip: !data?.id // Skip if no eventData (could also include to check isRecurring once its added to interface)
  });

  // Fetch tenant data if event has a tenantId
  const {
    data: tenantData,
    isLoading: isLoadingTenant
  } = useGetTenantByIdQuery(data?.tenantId || "", {
    skip: !data?.tenantId // Skip if no tenantId
  });

  const {
    data: venueData,
    isLoading: isLoadingVenue,
  } = useGetVenueByIdQuery(data?.venueId || "", {
    skip: !data?.venueId
  });

  const { data: eventTypes } = useGetEventTypesQuery(
    data?.tenantId ? { tenantId: data.tenantId } : undefined,
    { skip: !data }
  );

  // Function to get type names from IDs
  const getTypeNames = (eventTypeId: number, eventSubTypeId: number) => {
    const eventType = eventTypes?.find(type => type.eventTypeId === eventTypeId);
    const eventSubType = eventType?.subTypes?.find(subType => subType.eventSubTypeId === eventSubTypeId);
    return {
      typeName: eventType?.eventTypeName || data?.eventTypeName || data?.eventType?.name,
      subTypeName: eventSubType?.eventSubTypeName || data?.eventSubTypeName || data?.eventType?.subType?.name
    };
  };

  const [deleteEvent] = useDeleteEventMutation();
  const [deleteEventAndRecurrences] = useDeleteEventAndRecurrentsMutation();
  const [activateEvent] = useActivateEventMutation();

  const hideAddHostDialog = () => setHostToAdd(undefined);
  const hideDuplicateEventDialog = () => setDuplicateEvent(undefined);
  const hideDeleteEventDialog = () => setEventToDelete(undefined);
  const hideChangeEventStateDialog = () => setShowChangeEventStateDialog(false);
  const hideCancelEventDialog = () => setShowCancelEventDialog(false);
  const validateDate = data && correctDateAndHours(data) < new Date();
  // Sólo un evento cobrado por GRUPS puede generar un reembolso al dar de baja.
  const isPaidEvent = data?.paymentType === "GRUPS" && !!data?.inscription;

  const handleActivateEvent = async (id: number | undefined) => {
    if (!id) return;

    try {
      await activateEvent(id).unwrap();
      hideChangeEventStateDialog();
    } catch (err: any) {
      console.error("Failed to change event state:", err);
      const reason =
        err?.data?.error?.message ||
        err?.data?.message ||
        err?.error ||
        "el servidor rechazó el cambio de estado";
      toast.error(`No se pudo cambiar el estado del evento: ${reason}`);
    }
  };

  const handleDeleteEvent = (id: number) => {
    if (deleteWithRecurrents && recurrentEvents && recurrentEvents.length > 1) {
      deleteEventAndRecurrences(id)
        .unwrap()
        .then(() => {
          //-1 because recurrentEvents arr contains base event 
          //(event currently being modified)
          const recurrentAmount = recurrentEvents ? recurrentEvents.length : 0
          toast.success(`El evento y sus recurrencias (${(recurrentAmount - 1)}) han sido eliminados exitosamente`);
          navigate(-1);
        })
        .catch((res) => {
          const errorMessage = res.data?.error?.message || res.data?.errorMessage || "No se pudo eliminar el evento o las recurrencias";
          toast.error(errorMessage)
        })
        .finally(() => hideDeleteEventDialog())
    }
    else {
      deleteEvent(id)
        .unwrap()
        .then(() => {
          toast.success("Evento eliminado exitosamente!");
          navigate(-1);
        })
        .catch((res) => {
          const errorMessage = res.data?.error?.message || res.data?.errorMessage || "No se pudo eliminar el evento";
          toast.error(errorMessage)
        })
        .finally(() => hideDeleteEventDialog());
    }

  };

  if (isLoading)
    return (
      <div className="p-card gap-4 flex flex-column justify-content-center align-items-center p-3 min-h-11rem p-8">
        <h1 className="text-center text-3xl font-bold mb-6">
          Detalle del evento
        </h1>
        <ProgressSpinner />
      </div>
    );

  return (
    <>
      <Breadcrumb items={[{ label: "Eventos", to: "/events" }, { label: data?.name ?? "Detalle" }]} />
    <div className="p-card gap-4  p-3">
      <div className="flex w-full gap-4 justify-content-end">
        <Button
          icon="pi pi-pencil"
          severity="success"
          onClick={() => navigate(`/events/${data?.id}/edit`)}
          disabled={validateDate}
          tooltip="Editar"
          tooltipOptions={{ position: "left" }}
        />
        <Button
          icon="pi pi-clone"
          severity="warning"
          onClick={() => setDuplicateEvent(data as Event | undefined)}
          tooltip="Duplicar"
          tooltipOptions={{ position: "left" }}
        />
        <Button
          icon={data?.active ? "pi pi-eye-slash" : "pi pi-eye"}
          severity={data?.active ? "danger" : "success"}
          onClick={() =>
            data?.active
              ? setShowCancelEventDialog(true)
              : setShowChangeEventStateDialog(true)
          }
          disabled={validateDate || (!data?.active && !!data?.deactivatedAt)}
          tooltip={
            data?.active
              ? "Desactivar"
              : data?.deactivatedAt
                ? "Un evento desactivado no puede ser reactivado"
                : "Activar"
          }
          tooltipOptions={{ position: "left" }}
        />
        <Button
          icon="pi pi-times"
          severity="danger"
          onClick={() => setEventToDelete(data)}
          tooltip="Eliminar evento"
          tooltipOptions={{ position: "left" }}
        />
      </div>
      <h1 className="text-center text-3xl font-bold mb-3">{data?.name}</h1>
      <EventCancellationDetails
        eventId={parseInt(eventId || "0")}
        isEventActive={data ? !!data.active : undefined}
      />
      {data?.featured && (
        <div className="flex justify-content-center mb-3">
          <Badge
            value={`⭐ Destacado${data.featuredOrder ? ` #${data.featuredOrder}` : ""}`}
            size="xlarge"
          />
        </div>
      )}
      <div className="grid">
        <div className="col-6 md:col-4 md:my-3 flex flex-column align-items-center">
          <Badge
            className="md:hidden"
            value={
              data && correctDateAndHours(data) < new Date()
                ? "Finalizado"
                : data?.active
                  ? "Estado: Activo"
                  : "Estado: Inactivo"
            }
            severity={
              data && correctDateAndHours(data) < new Date()
                ? "warning"
                : data?.active
                  ? "success"
                  : "danger"
            }
          />
          <Badge
            className="hidden md:block"
            value={
              data && correctDateAndHours(data) < new Date()
                ? "Finalizado"
                : data?.active
                  ? "Estado: Activo"
                  : "Estado: Inactivo"
            }
            size={"xlarge"}
            severity={
              data && correctDateAndHours(data) < new Date()
                ? "warning"
                : data?.active
                  ? "success"
                  : "danger"
            }
          />
        </div>
        <div className="col-6 md:col-4 md:my-3 flex flex-column align-items-center">
          <Badge className="md:hidden" value={`Cupos: ${data?.amount}`} />
          <Badge
            className="hidden md:block"
            value={`Cupos: ${data?.amount}`}
            size={"xlarge"}
          />
        </div>
        <div className="col-12 md:col-4 md:my-3 flex flex-column align-items-center">
          <Badge
            className="md:hidden"
            value={`Disponibles: ${data?.available}`}
            severity={"warning"}
          />
          <Badge
            className="hidden md:block"
            value={`Disponibles: ${data?.available}`}
            size={"xlarge"}
            severity={"warning"}
          />
        </div>
        {/* GTK-802 — `lg`, no `md`: la columna hermana (el carrusel, más abajo) es
            `col-12 lg:col-5` porque su caja mide 400px fijos y no entra en 5/12 de un
            viewport md. Mientras ésta era `md:col-6`, entre 768 y 991px se llevaba
            media fila y el carrusel bajaba al renglón siguiente, dejando el bloque del
            host con medio renglón vacío al lado. Ahora las dos cambian en el mismo
            breakpoint. (Queda 1/12 libre a partir de 992px: 6+5; es cosmético y
            preexistente, no lo toco sin Diseño.) */}
        <div className="col-12 lg:col-6 grid">
          <div className="col-12 mb-2">
            {data?.hostLastName && data?.hostFirstName && (
              <>
                <div className="flex align-items-center mb-2 gap-4 mt-4">
                  {/* GTK-802 — misma caja fija que las miniaturas de EventListDV.
                      Antes era un <Image height="100" width="auto">: `width="auto"`
                      no es un valor válido del atributo HTML, así que el ancho salía
                      del aspect ratio de cada foto (150px la default 3:2, 133px una
                      subida a 4:3) y el nombre del host se corría de lugar según el
                      evento, además de saltar al cargar. El contenedor pasó de
                      align-items-end a center: con `end` el nombre quedaba clavado
                      al pie de una foto de 100px, con ~60px de aire arriba. */}
                  <div
                    className="flex-shrink-0 border-round overflow-hidden surface-100"
                    style={{ width: "100px", height: "100px" }}
                  >
                    <img
                      src={getEventImageSrc(data?.hostImageFormat, data?.hostImageBase64)}
                      alt={`Foto de ${data?.hostFirstName} ${data?.hostLastName}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      onError={(e) => {
                        e.currentTarget.src = "/assets/images/Image-not-found.png";
                      }}
                    />
                  </div>
                  <div className="flex align-items-center">
                    <h2 className="text-xl font-normal m-0 p-0  underline">
                      <b>Host:</b> {data?.hostLastName}, {data?.hostFirstName}
                    </h2>
                    <Button
                      icon="pi pi-pencil"
                      severity="success"
                      rounded
                      text
                      onClick={() => setHostToAdd(parseInt(eventId || "0"))}
                    />
                  </div>
                </div>
                <span className="font-medium">
                  Descripción del host: {data?.hostDescription}
                </span>
              </>
            )}
            <div className="flex flex-column font-medium gap-3 mt-4">
              <span className="text-xl">
                <i className="pi pi-calendar-clock mr-2 text-xl" />
                {formatDateWithoutSeconds(data?.startDate)}
              </span>
              {data?.inscriptionStartDate && (
                <span className="text-xl">
                  <i className="pi pi-calendar-plus mr-2 text-xl" />
                  Inicio inscripción: {formatDateWithoutSeconds(data.inscriptionStartDate)}
                </span>
              )}
              {data?.registrationDeadline != null && data?.startDate && (
                <span className="text-xl">
                  <i className="pi pi-calendar-minus mr-2 text-xl" />
                  Cierre inscripción: {formatDateWithoutSeconds(
                    new Date(new Date(data.startDate).getTime() - data.registrationDeadline * 60 * 1000).toISOString()
                  )}
                </span>
              )}
              <span className="text-xl">
                <i className="pi pi-map-marker mr-2 text-xl" />
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${data?.address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary"
                >
                  {data?.address}
                </a>
              </span>
            </div>
            <div className="mt-4 lg:mt-2">
              <span className="font-medium text-lg mr-4">
                <i className="pi pi-tag text-lg mr-2"></i>
                Tipo: {getTypeNames(data?.eventTypeId || 0, data?.eventSubTypeId || 0).typeName}
              </span>
              <span className="font-medium text-lg">
                <i className="pi pi-tag text-lg mr-2"></i>
                Sub tipo: {getTypeNames(data?.eventTypeId || 0, data?.eventSubTypeId || 0).subTypeName}
              </span>
            </div>
            <div className="mt-4 lg:mt-2">
              <span className="font-medium text-lg">
                <i className="pi pi-building text-lg mr-2"></i>
                Tenant: {isLoadingTenant ? "Cargando..." : (tenantData?.provider || "Desconocido")}
              </span>
            </div>
            <div className="flex flex-wrap font-medium gap-4 mt-4">
              <span>
                <i className="pi pi-dollar mr-2" />
                Costo: {data?.price} / Inscripcion:{" "}
                {data?.inscription}
              </span>
            </div>
            <div className="flex flex-wrap font-medium gap-4  mt-4">
              <span className="w-15rem">
                <i className="pi pi-clock mr-2" />
                Duración: {extractHoursAndMinutes(data?.duration)}hs
              </span>
              <span>
                <i className="pi pi-home mr-2" />
                Establecimiento: {data?.space}
              </span>
            </div>
            <div className="flex flex-wrap font-medium gap-4 mt-4">
              <span className="w-15rem">
                <i className="pi pi-user mr-2" />
                Host participa: {data?.hostAssists ? "Sí" : "No"}
              </span>
              <span>
                <i className="pi pi-users mr-2" />
                Invitados por persona: {data?.guests}
              </span>
            </div>

          </div>
        </div>
        <div className="col-12 lg:col-5 flex justify-content-center align-items-center my-2 ">
          {data?.imageUrls && data.imageUrls.length > 0 ? (
            <div style={{ width: '400px', height: '250px', overflow: 'hidden', position: 'relative' }}>
              <Swiper
                modules={[Navigation, Pagination]}
                spaceBetween={0}
                slidesPerView={1}
                navigation={true}
                pagination={{ clickable: true }}
                style={{ width: '100%', height: '100%' }}
                allowTouchMove={true}
                grabCursor={true}
                centeredSlides={true}
                loop={false}
                watchSlidesProgress={true}
                className="image-carousel"
              >
                {/* Include the original event image as first slide if it exists */}
                {(data?.imageBase64 || data?.imageFormat) && (
                  <SwiperSlide key={`${data?.id}-slide-original`}>
                    <div style={{
                      width: '400px',
                      height: '250px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}>
                      <img
                        src={getEventImageSrc(data?.imageFormat, data?.imageBase64)}
                        alt={`${data?.name} - Principal`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          position: 'absolute',
                          top: 0,
                          left: 0
                        }}
                      />
                    </div>
                  </SwiperSlide>
                )}
                {/* Include all additional images from imageUrls */}
                {data.imageUrls.map((imageUrl, index) => (
                  <SwiperSlide key={`${data?.id}-slide-${index}`}>
                    <div style={{
                      width: '400px',
                      height: '250px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}>
                      <img
                        src={imageUrl}
                        alt={`${data?.name} - ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          position: 'absolute',
                          top: 0,
                          left: 0
                        }}
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          ) : (
            <Image
              src={getEventImageSrc(data?.imageFormat, data?.imageBase64)}
              height="200"
              width="auto"
              alt={data?.name}
            />
          )}
          {/* <img
            className="w-9 sm:w-16rem xl:w-18rem shadow-2 block xl:block mx-auto border-round"
            src={`https://cdn.pixabay.com/photo/2018/04/26/16/39/beach-3352363_960_720.jpg`}
            alt={data?.name}
          /> */}
        </div>
      </div>
      <div className="col-12 mt-4 pt-4 border-top-1 surface-border">
        <h2 className="text-xl m-0 mb-3">Venue</h2>
        {data?.venueId ? (
          isLoadingVenue ? (
            <span className="text-color-secondary">Cargando venue...</span>
          ) : venueData ? (
            <div className="grid">
              <div className="col-12 md:col-4">
                <div className="text-color-secondary text-sm">Nombre</div>
                <button
                  type="button"
                  className="p-link font-medium text-lg text-left"
                  onClick={() => navigate(`/venues/${venueData.id}`)}
                >
                  {venueData.name}
                </button>
              </div>
              <div className="col-12 md:col-5">
                <div className="text-color-secondary text-sm">Dirección</div>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venueData.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-lg text-primary"
                >
                  {venueData.address}
                </a>
              </div>
              <div className="col-6 md:col-3">
                <div className="text-color-secondary text-sm">Capacidad máxima</div>
                <div className="font-medium text-lg">
                  {venueData.maxCapacity ?? "Sin definir"}
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div className="text-color-secondary text-sm">Coordenadas</div>
                <div className="font-medium">
                  {venueData.latitude}, {venueData.longitude}
                </div>
              </div>
              <div className="col-12 md:col-8">
                <div className="text-color-secondary text-sm">Contactos</div>
                <div className="font-medium">
                  {venueData.contacts?.length
                    ? venueData.contacts.map((contact) => contact.name).join(", ")
                    : "Sin contactos cargados"}
                </div>
              </div>
            </div>
          ) : (
            <span className="text-color-secondary">
              Venue #{data.venueId} no encontrado.
            </span>
          )
        ) : (
          <span className="text-color-secondary">Sin venue asignado.</span>
        )}
      </div>
      <div className="col">
        {data?.description && (
          <h2 className="text-xl font-normal">
            Descripción: {data?.description}
          </h2>
        )}
        {data?.rules && (
          <h2 className="text-xl font-normal">Reglas: {data?.rules}</h2>
        )}
      </div>
      <InscriptionsDT
        eventId={parseInt(eventId || "0")}
        mode="user"
        validateDate={validateDate}
        isPaidEvent={isPaidEvent}
      />
      <InscriptionsDT
        eventId={parseInt(eventId || "0")}
        mode="fictional"
        validateDate={validateDate}
      />
      <EventMessagesDV
        eventId={parseInt(eventId || "0")}
        validateDate={validateDate}
      />
      <EventCancelledDV eventId={parseInt(eventId || "0")} />
      <EventInterestDT eventId={parseInt(eventId || "0")} />
      {hostToAdd && (
        <Dialog
          header="Seleccionar host"
          visible={!!hostToAdd}
          style={{ width: "90vw" }}
          onHide={hideAddHostDialog}
        >
          <SingleSelectUser
            eventId={hostToAdd}
            hostAssists={!!data?.hostAssists}
            handleClose={() => {
              hideAddHostDialog();
            }}
          />
        </Dialog>
      )}
      {duplicateEvent && (
        <Dialog
          header="Duplicar evento"
          visible={!!duplicateEvent}
          style={{ width: "40vw" }}
          onHide={hideDuplicateEventDialog}
        >
          <DuplicateEventForm
            event={duplicateEvent}
            handleClose={hideDuplicateEventDialog}
          />
        </Dialog>
      )}
      {eventToDelete && (
        <Dialog
          header="Eliminar evento"
          visible={!!eventToDelete}
          style={{ width: "50vw" }}
          onHide={hideDeleteEventDialog}
          footer={
            <div>
              <Button
                severity="danger"
                label="Eliminar"
                onClick={() => handleDeleteEvent(eventToDelete.id)}
              />
              <Button
                severity="secondary"
                label="Cancelar"
                onClick={hideDeleteEventDialog}
              />
            </div>
          }
        >
          <h1 className="text-3xl text-red-500 m-0 p-0">
            <i className="pi pi-exclamation-triangle"></i> ¡Cuidado!
          </h1>
          <p className="m-0 p-0">
            ¿Esta seguro que desea eliminar el evento{" "}
            <b>{eventToDelete?.name}</b>?
          </p>
          {/* only show radio buttons for delete if the event is recurring */}
          {/* isArray is present because recurringEvents can be undefined because it comes from a fetch hook */}
          {/* check if length > 1 (if it's only one then this is the last instance of recurrence, therefore no more to delete) */}
          {Array.isArray(recurrentEvents) && recurrentEvents.length > 1 && (
            <>
              <div className="field-radiobutton mt-3">
                <label htmlFor="onlyThis">Eliminar solamente <strong>este evento</strong></label>
                <RadioButton
                  inputId="onlyThis"
                  name="removeScope"
                  value={false}
                  onChange={(e) => setDeleteWithRecurrents(e.value)}
                  checked={deleteWithRecurrents === false}
                />
              </div>

              <div className="field-radiobutton mt-3">
                <label htmlFor="allRecurrences">Eliminar este evento y <strong>todas sus recurrencias</strong></label>
                <RadioButton
                  inputId="allRecurrences"
                  name="removeScope"
                  value={true}
                  onChange={(e) => setDeleteWithRecurrents(e.value)}
                  checked={deleteWithRecurrents === true}
                />
              </div>
            </>
          )}


        </Dialog>

      )}
      {showChangeEventStateDialog && (
        <Dialog
          header="Activar evento"
          visible={!!showChangeEventStateDialog}
          style={{ width: "40vw" }}
          onHide={hideChangeEventStateDialog}
          footer={
            <div>
              <Button
                severity="success"
                label="Activar"
                onClick={() => handleActivateEvent(data?.id)}
              />
              <Button
                severity="secondary"
                label="Cancelar"
                onClick={hideChangeEventStateDialog}
              />
            </div>
          }
        >
          <p>Está por activar este evento. Pasará a estar visible en la App.</p>
        </Dialog>
      )}
      {showCancelEventDialog && (
        <CancelEventDialog
          visible={showCancelEventDialog}
          event={data}
          onHide={hideCancelEventDialog}
        />
      )}


    </div>
    </>
  );
};
